import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@alphabait/ui' // chart'
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts'
import { BaseQuestionRendererProps } from './types'
import { getChartColors } from '../utils/colors'
import { ChartWrapper, ChartEmptyState } from './shared'
import { useTranslation } from 'react-i18next'

const MAX_SEGMENTS = 8

export function MultiSelectSemiDonut({ question }: BaseQuestionRendererProps) {
  const { t } = useTranslation()

  const buckets = question.aggregates?.buckets || []
  const totalRespondents = question.totalRespondents || 0
  const totalSelections = buckets.reduce((sum, bucket) => sum + bucket.count, 0)

  if (buckets.length === 0) {
    return (
      <ChartWrapper
        title={question.label}
        description={t('dashboard.charts.multi_select_distribution')}
      >
        <ChartEmptyState message={t('dashboard.charts.no_responses_yet')} />
      </ChartWrapper>
    )
  }

  // Keep the chart readable by collapsing the long tail into an "Other" bucket.
  let displayBuckets = buckets.slice()
  if (buckets.length > MAX_SEGMENTS) {
    const visible = buckets.slice(0, MAX_SEGMENTS - 1)
    const otherCount = buckets
      .slice(MAX_SEGMENTS - 1)
      .reduce((sum, bucket) => sum + bucket.count, 0)
    displayBuckets = [
      ...visible,
      { label: 'Other', value: 'other', count: otherCount },
    ]
  }

  const colors = getChartColors()

  const slugifyKey = (value: string, index: number) => {
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    return slug ? `${slug}-${index}` : `option-${index}`
  }

  const chartConfig: ChartConfig = {}
  const radialDataKeyed: Record<string, number> = {}

  displayBuckets.forEach((bucket, index) => {
    const keySource = bucket.value || bucket.label || `option-${index}`
    const dataKey = slugifyKey(keySource, index)
    radialDataKeyed[dataKey] = bucket.count
    chartConfig[dataKey] = {
      label: bucket.label,
      color: colors[index % colors.length],
    }
  })

  const radialData = [
    {
      name: 'Selections',
      ...radialDataKeyed,
    },
  ]

  return (
    <ChartWrapper
      title={question.label}
      description={t('dashboard.charts.multi_select_distribution_count', {
        totalRespondents,
        totalSelections,
      })}
      className="flex flex-col p-0"
      contentClassName="items-start"
    >
      <div className="flex justify-between gap-8 flex-col md:flex-row">
        <ChartContainer
          config={chartConfig}
          className="w-full max-w-[360px] h-[250px]"
        >
          <RadialBarChart
            data={radialData}
            innerRadius={85}
            outerRadius={275}
            startAngle={180}
            endAngle={0}
          >
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
              formatter={(value: number, key: string) => {
                const percentage =
                  totalSelections > 0
                    ? ((Number(value) / totalSelections) * 100).toFixed(1)
                    : '0.0'
                const entry = Object.entries(chartConfig).find(
                  ([configKey]) => configKey === key,
                )
                const label = entry?.[1].label ?? key
                return [`${value} selections (${percentage}%)`, label]
              }}
            />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 5}
                          className="fill-foreground text-xl font-semibold"
                        >
                          {totalSelections}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 12}
                          className="fill-muted-foreground text-xs"
                        >
                          {t('dashboard.charts.people_answered', {
                            count: totalRespondents,
                          })}
                        </tspan>
                      </text>
                    )
                  }
                  return null
                }}
              />
            </PolarRadiusAxis>
            {Object.keys(radialDataKeyed).map((dataKey) => (
              <RadialBar
                key={dataKey}
                dataKey={dataKey}
                stackId="multi-select"
                className="stroke-transparent stroke-2"
                fill={`var(--color-${dataKey})`}
              />
            ))}
          </RadialBarChart>
        </ChartContainer>
      </div>

      <div className="w-full space-y-2 text-xs md:w-[220px] items-start max-h-52 h-52 overflow-y-auto">
        <div className="text-xs font-medium text-muted-foreground mb-1.5">
          Legend
        </div>
        {displayBuckets.map((bucket, index) => {
          const keySource = bucket.value || bucket.label || `option-${index}`
          const dataKey = slugifyKey(keySource, index)
          const percentage =
            totalSelections > 0
              ? ((bucket.count / totalSelections) * 100).toFixed(1)
              : '0.0'
          return (
            <div key={dataKey} className="flex items-start gap-2">
              <span
                className="mt-1 inline-flex h-3 w-3 flex-shrink-0 rounded-full"
                style={{ backgroundColor: colors[index % colors.length] }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className="font-medium leading-tight text-xs"
                  title={bucket.label}
                >
                  {bucket.label}
                </div>
                <div className="text-muted-foreground text-xs mt-0.5">
                  {bucket.count} ({percentage}%)
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </ChartWrapper>
  )
}
